import React, { useMemo, useState } from "react";
import {
    Card,
    Calendar,
    Modal,
    Form,
    Select,
    InputNumber,
    Button,
    Tag,
    Space,
    Tooltip,
    Typography,
    message,
    Popconfirm,
} from "antd";
import {
    InfoCircleOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import TitleHead from "../components/Customs/TitleHead";

const { Text } = Typography;

const roomCategoryOptions = [
    { label: "Luxury", value: "luxury" },
    { label: "Standard", value: "standard" },
    { label: "Deluxe", value: "deluxe" },
    { label: "Suite", value: "suite" },
    { label: "Family Room", value: "family_room" },
];

const SeasonalPricePage = () => {
    const [form] = Form.useForm();

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [editingItem, setEditingItem] = useState(null);

    const [seasonalPrices, setSeasonalPrices] = useState([]);

    const selectedDateKey = selectedDate
        ? dayjs(selectedDate).format("YYYY-MM-DD")
        : null;

    const categoryLabelMap = useMemo(() => {
        return roomCategoryOptions.reduce((acc, item) => {
            acc[item.value] = item.label;
            return acc;
        }, {});
    }, []);

    const openAddModal = (date) => {
        setSelectedDate(date);
        setEditingItem(null);
        form.resetFields();
        setModalOpen(true);
    };

    const openEditModal = (item) => {
        setSelectedDate(dayjs(item.date));
        setEditingItem(item);

        form.setFieldsValue({
            room_categories: [item.room_category],
            hike_percent: item.hike_percent,
        });

        setModalOpen(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            if (editingItem) {
                const category = values.room_categories[0];

                const duplicate = seasonalPrices.some(
                    (item) =>
                        item.id !== editingItem.id &&
                        item.date === selectedDateKey &&
                        item.room_category === category
                );

                if (duplicate) {
                    message.warning(
                        "Price hike already exists for this date and room category."
                    );
                    return;
                }

                setSeasonalPrices((prev) =>
                    prev.map((item) =>
                        item.id === editingItem.id
                            ? {
                                ...item,
                                id: `${selectedDateKey}-${category}`,
                                date: selectedDateKey,
                                room_category: category,
                                hike_percent: values.hike_percent,
                            }
                            : item
                    )
                );

                message.success("Seasonal price updated.");
            } else {
                const newItems = values.room_categories.map((category) => ({
                    id: `${selectedDateKey}-${category}`,
                    date: selectedDateKey,
                    room_category: category,
                    hike_percent: values.hike_percent,
                }));

                const duplicate = newItems.find((newItem) =>
                    seasonalPrices.some(
                        (oldItem) =>
                            oldItem.date === newItem.date &&
                            oldItem.room_category === newItem.room_category
                    )
                );

                if (duplicate) {
                    message.warning(
                        "Price hike already exists for this date and room category."
                    );
                    return;
                }

                setSeasonalPrices((prev) => [...prev, ...newItems]);
                message.success("Seasonal price added.");
            }

            setModalOpen(false);
            form.resetFields();
            setEditingItem(null);
        } catch (error) {
            console.log(error);
        }
    };

    const handleDelete = (id) => {
        setSeasonalPrices((prev) => prev.filter((item) => item.id !== id));
        message.success("Seasonal price removed.");
    };

    const getPricesByDate = (date) => {
        const dateKey = dayjs(date).format("YYYY-MM-DD");
        return seasonalPrices.filter((item) => item.date === dateKey);
    };

    const dateCellRender = (date) => {
        const list = getPricesByDate(date);

        if (!list.length) return null;

        return (
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
                {list.map((item) => (
                    <div
                        key={item.id}
                        style={{
                            background: "#f0f7ff",
                            border: "1px solid #d6e8ff",
                            borderRadius: 6,
                            padding: "4px 6px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 6,
                        }}
                    >
                        <Tag color="blue" style={{ margin: 0 }}>
                            {categoryLabelMap[item.room_category]} +{item.hike_percent}%
                        </Tag>

                        <Space size={4}>
                            <Button
                                type="text"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openEditModal(item);
                                }}
                            />

                            <Popconfirm
                                title="Remove this price hike?"
                                onConfirm={(e) => {
                                    e?.stopPropagation?.();
                                    handleDelete(item.id);
                                }}
                            >
                                <Button
                                    danger
                                    type="text"
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </Popconfirm>
                        </Space>
                    </div>
                ))}
            </Space>
        );
    };

    const selectedCategoriesForDate = seasonalPrices
        .filter(
            (item) =>
                item.date === selectedDateKey &&
                item.id !== editingItem?.id
        )
        .map((item) => item.room_category);

    const roomCategoryOptionsWithDisabled = roomCategoryOptions.map((option) => ({
        ...option,
        disabled: selectedCategoriesForDate.includes(option.value),
    }));

    return (
        <>
            <Card
                title={
                    <TitleHead
                        h1="Smart Seasonal Pricing"
                        h2="Configure dynamic room pricing for every season and special event."
                    />
                }
            >
                <Calendar
                    onSelect={openAddModal}
                    cellRender={(date, info) => {
                        if (info.type === "date") return dateCellRender(date);
                        return info.originNode;
                    }}
                />
            </Card>

            <Modal
                title={
                    editingItem
                        ? "Edit Seasonal Price"
                        : `Add Seasonal Price - ${selectedDateKey
                            ? dayjs(selectedDateKey).format("DD-MM-YYYY")
                            : ""
                        }`
                }
                open={modalOpen}
                onCancel={() => {
                    setModalOpen(false);
                    setEditingItem(null);
                    form.resetFields();
                }}
                footer={[
                    <Button key="cancel" onClick={() => setModalOpen(false)}>
                        Cancel
                    </Button>,
                    <Button key="save" type="primary" onClick={handleSave}>
                        {editingItem ? "Update" : "Save"}
                    </Button>,
                ]}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Room Category"
                        name="room_categories"
                        rules={[
                            {
                                required: true,
                                message: "Please select room category",
                            },
                        ]}
                    >

                        <Select
                            mode={editingItem ? undefined : "multiple"}
                            size="large"
                            placeholder="Select room category"
                            options={roomCategoryOptionsWithDisabled}
                        />
                    </Form.Item>

                    <Form.Item
                        label={
                            <Space>
                                Price Hike (%)
                                <Tooltip title="On this date, selected room price will increase by this percentage.">
                                    <InfoCircleOutlined style={{ color: "#1677ff" }} />
                                </Tooltip>
                            </Space>
                        }
                        name="hike_percent"
                        rules={[
                            {
                                required: true,
                                message: "Please enter price hike percentage",
                            },
                        ]}
                    >
                        <InputNumber
                            size="large"
                            min={1}
                            max={100}
                            addonAfter="%"
                            style={{ width: "100%" }}
                            placeholder="Enter hike percentage"
                        />
                    </Form.Item>

                    <Text type="secondary">
                        Same room category cannot be added twice for the same date.
                    </Text>
                </Form>
            </Modal>
        </>
    );
};

export default SeasonalPricePage;